import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  tableRowSize,
  stickyHeader,
  StatusType,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import {
  deductionsPenaltiesHeader,
  penaltyTypeColors,
} from "pages/PayRoll/PayRollHeaders";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";

export const Penalties = () => {
  const { domain, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [penaltiesData, setPenaltiesData] = useState([]);
  const containerRef = useRef(null);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Deductions",
    "Penalties"
  );
  const {
    result: apiData,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}payroll/deduction/penalty_details/?company_id=${topbarCompanyId}`
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setPenaltiesData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Penalties
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check details of penalties and other deductions applied to employee
            pay.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Penalty Records"
            filterHeaders={deductionsPenaltiesHeader}
            data={apiData}
            filteredData={penaltiesData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {deductionsPenaltiesHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {penaltiesData?.length === 0 ? (
                    <InfoMessage dataLength={apiData} />
                  ) : (
                    penaltiesData
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {deductionsPenaltiesHeader.map((head) => (
                            <Mui.TableCell>
                              {head.key === "penalty_type" ? (
                                <span
                                  style={StatusType(
                                    value.penalty_type,
                                    penaltyTypeColors
                                  )}
                                >
                                  {value.penalty_type}
                                </span>
                              ) : head.key === "basis" ? (
                                value?.basis ?? "N/A"
                              ) : (
                                value[head.key] ?? "0"
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {penaltiesData?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={penaltiesData?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
    </React.Fragment>
  );
};
